import { verifyAndLinkMemberId } from 'Api/VeteransAdvApi';
import { isSheerIdPlan } from './SheerIdHelpers';
import { getMemberVerify } from 'Api/AccountApi';
import {
  VA_1_YEAR_ID,
  VA_2_YEAR_ID,
  VA_CHURNED_PACKAGE_ID,
  VA_INITIAL_FREE_YEAR_ID,
  VA_MONTHLY_ID,
  VA_QUERY_PARAM,
} from './Constants';
import { getState } from './Store';
import { setVeteransAdvError, setVeteransAdvId } from 'State/Integrations';

export const VeteransAdvStatus = {
  INVALID: 0,
  VALID: 1,
  ERROR: 2,
};

export const getMemberId = () => {
  return getState('integrations').veteransAdv.member_id;
};

export const VeteransAdvErrors = {
  //Users with existing IAP or Bango subscriptions or invalid VA member ids
  NOT_ELIGIBLE: 'We are sorry, looks like you are not eligible for this plan',
  //Users with a SheerID Verification
  ALREADY_REDEEMED: 'Looks like you have already redeemed a Military offer',
  OTHER: 'There has been an error processing your request, please try again',
};

export const shouldGetVAPlans = async (isAuthenticated, subscription, bango) => {
  if (!isAuthenticated) return VeteransAdvStatus.INVALID;

  const { isChurnedUser, isNewUser, isIAPUser, hasActiveSubscription, activeSubscriptionId, purchaseType } =
    subscription;
  const { subscriptionId: isBango } = bango;

  const urlParams = new URLSearchParams(window.location.search);
  const queryMemberId = urlParams.get(VA_QUERY_PARAM);

  // validate user for veterans advantage/WeSalute in one of two ways in 1a or 1b:
  // a. call memberverify endpoint to see if user is already linked to a member id
  // b. if `va_member_id` url param is present, validate member id through verify endpoint
  //
  // if user is validated through url param (step b), check if they're an existing user. if so,
  //    check their subscription and proceed only if they have a web SKU and not sheer/bango/verizon/iap
  let verifiedUserForVA = false;
  try {
    // step a
    const verifyUserResponse = await getMemberVerify();
    const { verifier, currentStep } = verifyUserResponse;

    if (currentStep === 'success') {
      if (verifier === 'veteransadvantage') {
        verifiedUserForVA = true;

        // specifically, if user is sheer verified and is trying to access VA route by providing a member id
        //    redirect to error
      } else if (queryMemberId && verifier === 'sheerid') {
        setVeteransAdvError(VeteransAdvErrors.ALREADY_REDEEMED);
        return VeteransAdvStatus.ERROR;
      }
    }
  } catch (error) {
    // ignore error if the user is simply not found since we call verifyAndLinkMemberId below to verify their id
    if (error?.errorType !== 'Not Found') {
      setVeteransAdvError(VeteransAdvErrors.OTHER);
      return VeteransAdvStatus.ERROR;
    }
  }

  try {
    if (queryMemberId && !verifiedUserForVA) {
      // check if existing user and with a non-web SKU, if so resume normal flow without veterans advantage/WeSalute
      //    otherwise, proceed with verifying member id
      if (
        !isChurnedUser &&
        !isNewUser &&
        hasActiveSubscription &&
        (isSheerIdPlan(activeSubscriptionId) || isBango || isIAPUser || purchaseType === 'bango')
      ) {
        if (isSheerIdPlan(activeSubscriptionId)) {
          setVeteransAdvError(VeteransAdvErrors.ALREADY_REDEEMED);
        } else {
          setVeteransAdvError(VeteransAdvErrors.NOT_ELIGIBLE);
        }
        return VeteransAdvStatus.ERROR;
      }

      // step b
      // as long as api returns non-error response, it means member id is successfully linked
      await verifyAndLinkMemberId(queryMemberId);
      verifiedUserForVA = true;
    }
  } catch (error) {
    if (error?.errorMessage === 'the offer is already claimed') {
      setVeteransAdvError(VeteransAdvErrors.ALREADY_REDEEMED);
    } else if (error?.errorType === 'Not Found') {
      setVeteransAdvError(VeteransAdvErrors.NOT_ELIGIBLE);
    } else {
      setVeteransAdvError(VeteransAdvErrors.OTHER);
    }
    return VeteransAdvStatus.ERROR;
  }

  if (verifiedUserForVA) {
    setVeteransAdvId(queryMemberId);
  }

  return verifiedUserForVA ? VeteransAdvStatus.VALID : VeteransAdvStatus.INVALID;
};

export const isVeteransAdvPlan = (planId) => {
  return [VA_CHURNED_PACKAGE_ID, VA_INITIAL_FREE_YEAR_ID, VA_MONTHLY_ID, VA_1_YEAR_ID, VA_2_YEAR_ID].includes(planId);
};

export const isVAFreePlan = (planId) => {
  return VA_INITIAL_FREE_YEAR_ID === planId;
};
